import { useFormContext, useWatch } from 'react-hook-form';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { OwnerFormValue } from '../../formSchema';
import { useOwnerQuery } from '../../hooks';
import { OwnerSchainDataset, SchainLogicMode } from '../../model';
import { OwnerSchainCsvRow } from './OwnerSchainCsvRow';

export function OwnerSchainFields() {
  const { register, control, setValue } = useFormContext<OwnerFormValue>();
  const ownerId = useWatch({ control, name: 'id' });
  const logicMode = useWatch({ control, name: 'defaultSchainLogicMode' });
  const query = useOwnerQuery(ownerId);

  return (
    <DashboardPageFormBody
      width="lg"
      title={dashboardT('owners.tabs.schain')}
      description={dashboardT('owners.schain.description')}
    >
      <DashboardPageFormField
        label={dashboardT('owners.fields.schainMode')}
        hint={dashboardT('owners.hints.schainMode')}
        htmlFor="owner-schain-mode"
      >
        <Select
          value={logicMode}
          onValueChange={(value) => {
            setValue('defaultSchainLogicMode', value as SchainLogicMode, { shouldDirty: true });
          }}
        >
          <SelectTrigger id="owner-schain-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(SchainLogicMode).map((mode) => (
              <SelectItem key={mode} value={mode}>
                {schainModeText(mode)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DashboardPageFormField>
      <section className="border-input overflow-hidden rounded-md border">
        <header className="bg-muted/40 border-input border-b px-4 py-3">
          <h2 className="text-sm font-semibold">{dashboardT('owners.schain.mappings')}</h2>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {dashboardT('owners.schain.mappingsDescription')}
          </p>
        </header>
        <div className="divide-border divide-y">
          <OwnerSchainCsvRow
            ownerId={ownerId}
            dataset={OwnerSchainDataset.Direct}
            count={query.data?.directSchainRecords.length ?? 0}
          />
          <OwnerSchainCsvRow
            ownerId={ownerId}
            dataset={OwnerSchainDataset.Reseller}
            count={query.data?.resellerSchainRecords.length ?? 0}
          />
        </div>
      </section>
      <DashboardPageFormField
        label={dashboardT('owners.fields.resellerAllowlist')}
        hint={dashboardT('owners.hints.resellerAllowlist')}
        htmlFor="owner-reseller-allowlist"
      >
        <Textarea id="owner-reseller-allowlist" rows={5} {...register('resellerBundleAllowlist')} />
      </DashboardPageFormField>
    </DashboardPageFormBody>
  );
}

function schainModeText(mode: SchainLogicMode) {
  if (mode === SchainLogicMode.DirectOnly) return dashboardT('owners.schain.mode.directOnly');
  if (mode === SchainLogicMode.DirectPriority) {
    return dashboardT('owners.schain.mode.directPriority');
  }
  if (mode === SchainLogicMode.ResellerOnly) {
    return dashboardT('owners.schain.mode.resellerOnly');
  }
  return dashboardT('owners.schain.mode.resellerPriority');
}
